import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminBilling() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Record Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentType: 'monthly',
    paymentMethod: 'manual',
    notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Payment History Modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    fetchBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchBillingData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/billing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setTenants(data.tenants);
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  const openPaymentModal = (tenant) => {
    setSelectedTenant(tenant);
    setPaymentForm({
      amount: tenant.monthlyRate || '',
      paymentType: 'monthly',
      paymentMethod: 'manual',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setSavingPayment(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/tenants/${selectedTenant.id}/record-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(paymentForm)
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setShowPaymentModal(false);
      fetchBillingData(); // Refresh data
    } catch (err) {
      alert('Error recording payment: ' + err.message);
    } finally {
      setSavingPayment(false);
    }
  };

  const openHistoryModal = async (tenant) => {
    setSelectedTenant(tenant);
    setShowHistoryModal(true);
    setLoadingHistory(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/tenants/${tenant.id}/payment-history`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setPaymentHistory(data.payments);
    } catch (err) {
      alert('Error fetching payment history: ' + err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/payments/${paymentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      // Refresh payment history
      setPaymentHistory(paymentHistory.filter(p => p.id !== paymentId));
      // Also refresh main billing data in case it affects totals
      fetchBillingData();
    } catch (err) {
      alert('Error deleting payment: ' + err.message);
    }
  };

  const updateBillingStatus = async (tenantId, newStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/tenants/${tenantId}/billing-status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ billingStatus: newStatus })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      fetchBillingData(); // Refresh
    } catch (err) {
      alert('Error updating status: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getTierLabel = (tier) => {
    const labels = {
      free_trial: 'Free Trial',
      starter: 'Starter',
      pro: 'Pro',
      enterprise: 'Enterprise'
    };
    return labels[tier] || tier;
  };

  const filteredTenants = filterStatus === 'all'
    ? tenants
    : tenants.filter(t => t.billingStatus === filterStatus);

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link active">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
        <Link to="/master-admin/settings" className="admin-nav-link">Settings</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-section">
            <h2>Billing & Subscriptions</h2>

            {error && <div className="error-message">{error}</div>}

            {/* Stats Cards */}
            {stats && (
              <div className="stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="stat-card">
                  <div className="stat-value">{formatCurrency(stats.mrr)}</div>
                  <div className="stat-label">Monthly Recurring Revenue</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: stats.pastDueCount > 0 ? '#dc3545' : 'inherit' }}>
                    {stats.pastDueCount}
                  </div>
                  <div className="stat-label">Past Due</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{formatCurrency(stats.revenueThisMonth)}</div>
                  <div className="stat-label">Revenue This Month</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.totalTenants}</div>
                  <div className="stat-label">Total Tenants</div>
                </div>
              </div>
            )}

            {/* Filter */}
            <div className="admin-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Tenant Billing</h3>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                >
                  <option value="all">All Statuses</option>
                  <option value="current">Current</option>
                  <option value="past_due">Past Due</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Tenants Table */}
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Company</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Tier</th>
                      <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Monthly Rate</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Last Payment</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Next Due</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.map(tenant => (
                      <tr key={tenant.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: '600' }}>{tenant.companyName}</div>
                          {tenant.subdomain && (
                            <div style={{ fontSize: '0.85em', color: 'var(--color-text-light)' }}>
                              {tenant.subdomain}.deedstack.com
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px' }}>{getTierLabel(tenant.subscriptionTier)}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          {formatCurrency(tenant.monthlyRate)}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <select
                            value={tenant.billingStatus}
                            onChange={(e) => updateBillingStatus(tenant.id, e.target.value)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--color-border)',
                              /* Was 0.85em (~13.6px), under the iOS zoom
                                 threshold. This sits in a table cell, so it
                                 stays content-sized rather than full width. */
                              fontSize: '16px',
                              minHeight: '44px',
                              boxSizing: 'border-box',
                              backgroundColor: tenant.billingStatus === 'current' ? '#d4edda' :
                                tenant.billingStatus === 'past_due' ? '#f8d7da' :
                                tenant.billingStatus === 'suspended' ? '#fff3cd' : '#e2e3e5',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="current">Current</option>
                            <option value="past_due">Past Due</option>
                            <option value="suspended">Suspended</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td style={{ padding: '12px 8px' }}>{formatDate(tenant.lastPaymentDate)}</td>
                        <td style={{ padding: '12px 8px' }}>{formatDate(tenant.nextPaymentDate)}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <button
                            onClick={() => openPaymentModal(tenant)}
                            className="btn btn-primary btn-small"
                            style={{ marginRight: '4px' }}
                          >
                            Record Payment
                          </button>
                          <button
                            onClick={() => openHistoryModal(tenant)}
                            className="btn btn-secondary btn-small"
                          >
                            History
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredTenants.length === 0 && (
                      <tr>
                        <td colSpan="7" style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-light)' }}>
                          No tenants found with this billing status.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Record Payment Modal */}
      {showPaymentModal && selectedTenant && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px',
            maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0 }}>Record Payment</h3>
            <p style={{ color: 'var(--color-text-light)' }}>
              Recording payment for <strong>{selectedTenant.companyName}</strong>
            </p>

            <form onSubmit={handleRecordPayment}>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="form-label">
                  Payment Type *
                </label>
                <select
                  value={paymentForm.paymentType}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentType: e.target.value })}
                  className="form-select"
                >
                  <option value="setup_fee">Setup Fee</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="refund">Refund</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="form-label">
                  Payment Method
                </label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  className="form-select"
                >
                  <option value="card">Card</option>
                  <option value="invoice">Invoice</option>
                  <option value="manual">Manual</option>
                  <option value="stripe">Stripe</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label className="form-label">
                  Notes
                </label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows="3"
                  className="form-textarea"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingPayment}
                >
                  {savingPayment ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showHistoryModal && selectedTenant && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px',
            maxWidth: '700px', width: '90%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Payment History</h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5em', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>
            <p style={{ color: 'var(--color-text-light)', marginBottom: '16px' }}>
              <strong>{selectedTenant.companyName}</strong>
            </p>

            {loadingHistory ? (
              <div style={{ textAlign: 'center', padding: '24px' }}>Loading...</div>
            ) : paymentHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-text-light)' }}>
                No payment history found.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Amount</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Method</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Notes</th>
                    <th style={{ textAlign: 'center', padding: '8px', borderBottom: '2px solid var(--color-border)', width: '60px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map(payment => (
                    <tr key={payment.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '8px' }}>{formatDate(payment.paymentDate)}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <span style={{ color: payment.paymentType === 'refund' ? '#dc3545' : 'inherit' }}>
                          {payment.paymentType === 'refund' ? '-' : ''}{formatCurrency(payment.amount)}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textTransform: 'capitalize' }}>
                        {payment.paymentType.replace('_', ' ')}
                      </td>
                      <td style={{ padding: '8px', textTransform: 'capitalize' }}>{payment.paymentMethod}</td>
                      <td style={{ padding: '8px', color: 'var(--color-text-light)', fontSize: '0.9em' }}>
                        {payment.notes || '-'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => deletePayment(payment.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-error)',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            padding: '4px 8px'
                          }}
                          title="Delete payment"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <button onClick={() => setShowHistoryModal(false)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterAdminBilling;
